// comparable-grid.util.ts
import { ColDef, ValueGetterParams } from 'ag-grid-community';
import { Comparable, UnitMix } from '../../models/comparables.type';

const txt = 'agTextColumnFilter';
const num = 'agNumberColumnFilter';
const date = 'agDateColumnFilter';

const v = (path: string) => (p: ValueGetterParams<Comparable>) =>
  path.split('.').reduce<any>((acc, k) => (acc == null ? acc : acc[k as keyof typeof acc]), p.data as any) ?? '';



export const MANDATORY_MSG = 'Missing mandatory info';

export const isBlank = (v: any) =>
  v === null ||
  v === undefined ||
  (typeof v === 'string' && v.trim() === '');

export const mandatory = <T>(col: ColDef<T>): ColDef<T> => ({
  ...col,
  cellClassRules: {
    ...(col.cellClassRules ?? {}),
    'cell-mandatory-missing': (p) => isBlank(p.value),
  },
  tooltipValueGetter: (p) => (isBlank(p.value) ? MANDATORY_MSG : ''),
});

const baseCols: ColDef<Comparable>[] = [
  { headerName: 'Kind', valueGetter: p => p.data?.kind ?? p.data?.snapshot?.kind, filter: txt, minWidth: 110 },
  { headerName: 'Property Id', valueGetter: v('snapshot.propertyId'), filter: txt, minWidth: 140 },
  mandatory({ headerName: 'Property Name', valueGetter: v('snapshot.propertyName'), filter: txt, minWidth: 180,  cellRenderer: "agGroupCellRenderer" }),
  mandatory({ headerName: 'Address Line 1', valueGetter: v('snapshot.addressLine1'), filter: txt, minWidth: 200 }),
  mandatory({ headerName: 'City', valueGetter: v('snapshot.city'), filter: txt, minWidth: 140 }),
  mandatory({ headerName: 'State/Province', valueGetter: v('snapshot.state'), filter: txt, minWidth: 140 }),
  mandatory({ headerName: 'Zip Code/Postal Code', valueGetter: v('snapshot.zip'), filter: txt, minWidth: 160 }) 
  //{ headerName: 'Country', valueGetter: v('snapshot.country'), filter: txt, minWidth: 140 }
   

  // top-level comparable metadata
  // { headerName: 'Transaction Id', field: 'transactionId', filter: txt, minWidth: 160 },// Not in sales
  // { headerName: 'Rank', field: 'rank', filter: num, minWidth: 100 }, // Not in sales
  // { headerName: 'Weight', field: 'weight', filter: num, minWidth: 110 }, // Not in sales  
];

const salesCols: ColDef<Comparable>[] = [  
  mandatory({ headerName: 'Year Built', valueGetter: v('snapshot.yearBuilt'), filter: txt, minWidth: 120 }),  
  mandatory({ headerName: 'Number of Units', valueGetter: v('snapshot.numberOfUnits'), filter: txt, minWidth: 160 }),
  { headerName: 'Sale Date', valueGetter: v('snapshot.sales.saleDate'), filter: date, minWidth: 140 },
  { headerName: 'Sale Price', valueGetter: v('snapshot.sales.salePrice'), filter: txt, minWidth: 140 },
  { headerName: 'NOI (Total Amount)', valueGetter: v('snapshot.sales.noi'), filter: txt, minWidth: 160 },
  { headerName: 'Capitalization Rate', valueGetter: v('snapshot.sales.capRate'), filter: txt, minWidth: 170 },
  { headerName: 'Distance From Subject Property', field: 'distanceFromSubjectMiles', filter: txt, minWidth: 220 },
  { headerName: 'Year Last Renovated', valueGetter: v('snapshot.yearLastRenovated'), filter: txt, minWidth: 180 },
  { headerName: 'Net Cash Flow Amount', valueGetter: v('snapshot.sales.netCashFlowAmount'), filter: txt, minWidth: 190 },
  mandatory({ headerName: 'Comparison Overall', field: 'comparisonOverall', filter: txt, minWidth: 180 }),
  mandatory({ headerName: 'Adjustment Amount', field: 'adjustmentAmount', filter: txt, minWidth: 170 }),
  { headerName: 'Comp ID', valueGetter: v('compId'), filter: txt, minWidth: 130 },
  { headerName: 'Property Type', valueGetter: v('snapshot.propertyType'), filter: txt, minWidth: 160 }
];

const expenseCols: ColDef<Comparable>[] = [
  mandatory({ headerName: 'Number of Units', valueGetter: v('snapshot.numberOfUnits'), filter: txt, minWidth: 160 }),
  mandatory({ headerName: 'Year Built', valueGetter: v('snapshot.yearBuilt'), filter: txt, minWidth: 120 }), 
  { headerName: 'Year Last Renovated', valueGetter: v('snapshot.yearLastRenovated'), filter: txt, minWidth: 180 },
  { headerName: 'Effective Gross Revenue (Total Amount) - As Is', valueGetter: v('snapshot.expense.effectiveGrossRevenue'), filter: txt, minWidth: 320 },
  { headerName: 'Insurance', valueGetter: v('snapshot.expense.insurance'), filter: txt, minWidth: 140 },
  { headerName: 'Management Fees', valueGetter: v('snapshot.expense.managementFees'), filter: txt, minWidth: 160 },
  { headerName: 'General & Administrative', valueGetter: v('snapshot.expense.generalAdministrative'), filter: txt, minWidth: 200 },
  { headerName: 'Total Operating Expenses', valueGetter: v('snapshot.expense.totalOperatingExpenses'), filter: txt, minWidth: 210 },
  { headerName: 'Real Estate Taxes', valueGetter: v('snapshot.expense.realEstateTaxes'), filter: txt, minWidth: 170 },
  { headerName: 'NOI (Total Amount)', valueGetter: v('snapshot.expense.noi'), filter: txt, minWidth: 160 },
  { headerName: 'Distance From Subject Property', field: 'distanceFromSubjectMiles', filter: txt, minWidth: 220 },
  { headerName: 'Replacement Reserves', valueGetter: v('snapshot.expense.replacementReserves'), filter: txt, minWidth: 230 },
  { headerName: 'Comp ID', valueGetter: v('compId'), filter: txt, minWidth: 130 },
  { headerName: 'Property Type', valueGetter: v('snapshot.propertyType'), filter: txt, minWidth: 160 },
  { headerName: 'Operating Year', valueGetter: v('snapshot.expense.operatingYear'), filter: txt, minWidth: 150 },
  { headerName: 'Operating History Basis', valueGetter: v('snapshot.expense.operatingHistoryBasis'), filter: txt, minWidth: 150 },
  { headerName: 'Number of Months', valueGetter: v('snapshot.expense.numberOfMonths'), filter: txt, minWidth: 170 },
  { headerName: 'Starting Month', valueGetter: v('snapshot.expense.startingMonth'), filter: txt, minWidth: 160 },
  { headerName: 'Turn On Confidentiality', valueGetter: v('snapshot.expense.confidential'), filter: txt, minWidth: 200 },
  { headerName: 'Building Size Units Range', valueGetter: v('snapshot.expense.buildingSizeUnitsRange'), filter: txt, minWidth: 230 },
  { headerName: 'Rentable Square Feet Range', valueGetter: v('snapshot.expense.rentableSquareFeetRange'), filter: txt, minWidth: 230 },
];

const landCols: ColDef<Comparable>[] = [
  { headerName: 'Number Of Units', valueGetter: v('snapshot.numberOfUnits'), filter: num, minWidth: 160 },
  { headerName: 'Distance From Subject Property', field: 'distanceFromSubjectMiles', filter: txt, minWidth: 220 },
  { headerName: 'Comp ID', valueGetter: v('compId'), filter: txt, minWidth: 130 },
  { headerName: 'Property Type', valueGetter: v('snapshot.propertyType'), filter: txt, minWidth: 160 }
];

const rentCols: ColDef<Comparable>[] = [
  { headerName: 'Number of Units', valueGetter: v('snapshot.numberOfUnits'), filter: txt, minWidth: 160 },
  { headerName: 'Year Built', valueGetter: v('snapshot.yearBuilt'), filter: txt, minWidth: 120 }, 
  { headerName: 'Year Last Renovated', valueGetter: v('snapshot.yearLastRenovated'), filter: txt, minWidth: 180 },
  { headerName: 'Comp ID', valueGetter: v('compId'), filter: txt, minWidth: 130 },
  { headerName: 'Property Type', valueGetter: v('snapshot.propertyType'), filter: txt, minWidth: 160 },
  { headerName: 'Rental Survey Date', valueGetter: v('snapshot.rent.surveyDate'), filter: date, minWidth: 140 },
  { headerName: 'Lease Start Date', valueGetter: v('snapshot.rent.leaseStartDate'), filter: date, minWidth: 160 },
  { headerName: 'Suite', valueGetter: v('snapshot.rent.suite'), filter: txt, minWidth: 120 },
  { headerName: 'Tenant Size (Sq. Ft)', valueGetter: v('snapshot.rent.tenantSizeSqFt'), filter: txt, minWidth: 180 },
  { headerName: 'Tenant Name', valueGetter: v('snapshot.rent.tenantName'), filter: txt, minWidth: 160 },
  { headerName: 'Ground Leases', valueGetter: v('snapshot.rent.isGroundLease'), filter: txt, minWidth: 160 },
  { headerName: 'Site Area Leased', valueGetter: v('snapshot.rent.siteAreaLeased'), filter: txt, minWidth: 160 },
  { headerName: 'Initial Annual Rent', valueGetter: v('snapshot.rent.initialAnnualRent'), filter: txt, minWidth: 170 },
  mandatory({ headerName: 'Average Contract Rent', valueGetter: v('snapshot.rent.averageContractRent'), filter: txt, minWidth: 230 }),
  { headerName: 'Distance From Subject Property', field: 'distanceFromSubjectMiles', filter: txt, minWidth: 220 },
  //{ headerName: 'Compute Average Rent Amount', valueGetter: v('snapshot.rent.averageRentAmount'), filter: txt, minWidth: 230 },  
  {
    headerName: 'Unit Mixes',
    valueGetter: (p) => String(p.data?.snapshot?.rent?.unitMixes?.length ?? 0),
    filter: num,
    minWidth: 130,
  },
];

export const unitMixDetailCols: ColDef<UnitMix>[] = [
  mandatory({ headerName: 'Floor Plan Number of Units', headerTooltip: 'Floor Plan Number of Units', field: 'floorPlanNumberOfUnits', filter: txt, minWidth: 220 }),
  mandatory({ headerName: 'Number of Beds', headerTooltip: 'Number of Beds', field: 'beds', filter: txt, minWidth: 150 }),
  { headerName: 'Number of Baths Full', headerTooltip: 'Number of Baths Full', field: 'bathsFull', filter: txt, minWidth: 170 },
  { headerName: 'Number of Baths Half', headerTooltip: 'Number of Baths Half', field: 'bathsHalf', filter: txt, minWidth: 170 },
  mandatory({ headerName: 'Average Contract Rent per Unit Of Measure', headerTooltip: 'Average Contract Rent per Unit Of Measure', field: 'averageContractRentPerUnitOfMeasure', filter: txt, minWidth: 230 }),
];

export type GridKind = 'Sales' | 'Rent' | 'Expense' | 'Land';

export function buildComparableColumnDefs(kind: GridKind): ColDef<Comparable>[] {
  const byKind =
    kind === 'Sales' ? salesCols :
    kind === 'Rent' ? rentCols :
    kind === 'Expense' ? expenseCols :
    landCols;

  // Put the most-used filters first so they appear near the top of the Filters panel
  return [
    // reorder base to influence Filters panel order
    baseCols.find(c => c.headerName === 'Property Name')!,
    baseCols.find(c => c.headerName === 'Address Line 1')!,    
    baseCols.find(c => c.headerName === 'City')!,
    ...baseCols.filter(c =>
      !['Property Id', 'Property Name', 'Address Line 1', 'City', 'Kind'].includes(String(c.headerName))
    ),
    ...byKind,
  ].filter(Boolean) as ColDef<Comparable>[];
}